import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

const MIN_LEN = 6;

export default function ChangePassword() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  function validate() {
    const e = {};
    if (!form.current_password) e.current_password = "Enter your current password";
    if (!form.new_password) e.new_password = "Enter a new password";
    else if (form.new_password.length < MIN_LEN) e.new_password = `Minimum ${MIN_LEN} characters`;
    if (!form.confirm_password) e.confirm_password = "Confirm your new password";
    else if (form.confirm_password !== form.new_password) e.confirm_password = "Passwords do not match";
    if (form.current_password && form.new_password && form.current_password === form.new_password) {
      e.new_password = "New password must differ from current password";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(evt) {
    evt.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", form);
      toast.success("Password changed successfully. Please sign in again.");
      // Force logout for security best practice after password change.
      setTimeout(() => logout(), 800);
    } catch (e) {
      const detail = formatApiErrorDetail(e);
      if (detail?.toLowerCase().includes("current")) {
        setErrors({ current_password: detail });
      } else {
        toast.error(detail || "Failed to change password");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="change-password-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
          <KeyRound className="w-3.5 h-3.5 text-red-700" />
          Security
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1">Change Password</h1>
        <p className="text-sm text-slate-500 mt-1">
          Choose a strong password. Minimum {MIN_LEN} characters. You will be signed out after change.
        </p>
      </div>

      <Card className="p-6">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="cp-current">Current Password</Label>
            <Input
              id="cp-current"
              data-testid="input-current-password"
              type="password"
              autoComplete="current-password"
              value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            />
            {errors.current_password && (
              <div className="text-xs text-red-700 mt-1">{errors.current_password}</div>
            )}
          </div>

          <div>
            <Label htmlFor="cp-new">New Password</Label>
            <Input
              id="cp-new"
              data-testid="input-new-password"
              type="password"
              autoComplete="new-password"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            />
            {errors.new_password && (
              <div className="text-xs text-red-700 mt-1">{errors.new_password}</div>
            )}
          </div>

          <div>
            <Label htmlFor="cp-confirm">Confirm New Password</Label>
            <Input
              id="cp-confirm"
              data-testid="input-confirm-password"
              type="password"
              autoComplete="new-password"
              value={form.confirm_password}
              onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            />
            {errors.confirm_password && (
              <div className="text-xs text-red-700 mt-1">{errors.confirm_password}</div>
            )}
          </div>

          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 text-emerald-700 shrink-0" />
            <span>
              Passwords are hashed with bcrypt and never stored in plain text. After a successful change, your session ends and you must sign in again.
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => nav(-1)} disabled={saving} data-testid="btn-cancel-cp">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="btn-submit-change-password"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Change Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
